/**
 *  @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { format, addMonths, startOfMonth, startOfDay, differenceInMinutes } from 'date-fns';
import { omit, get, find, cloneDeep, map } from 'lodash';
import { Icon, Message } from 'semantic-ui-react';

import { Modal, Button } from '../../../../components';
import { convertMinutesToDateTime } from '../../../../lib/date';
import editFormValidate from '../../containers/form/validate';

import AppointmentForm from '../../containers/form/AppointmentForm';
import DeleteAppointmentButton from './DeleteAppointmentButton';
import CreateInvoiceButton from '../../containers/details/CreateInvoiceButton';
import ArchiveButton from '../../containers/details/ArchiveButton';

import InvoiceMessage from './InvoiceMessage';
import GroupedMessage from './GroupedMessage';

import { GET_MASTER_APPOINTMENTS_QUERY } from '../../../master-appointments/containers/queries';
import { GET_WORKING_DAYS } from '../../../master-appointments/containers/DateRange';
import { APPOINTMENT_FRAGMENT } from '../../containers/queries';

import type { Appointment } from '../../../../type';

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.schedule.form.AppointmentForm.existingsError',
        defaultMessage: 'This appointment does not exist',
    },
    timeOverlapError: {
        id: 'pages.schedule.form.AppointmentForm.startTimeError',
        defaultMessage: 'Time is overlapping with another appointment',
    },
    appointmentAlreadyExists: {
        id: 'pages.appointments.AppointmentForm.error.appointmentAlreadyExists',
        defaultMessage: 'Same appointment already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

// The query to update existing appointment with a new data
export const UPDATE_APPOINTMENT_QUERY = gql`
    mutation updateAppointment(
        $input: UpdateAppointmentInput!
        $lang: String!
        $salon: ID!
    ) {
        updateAppointment(input: $input) {
            appointment {
                id
                ...appointment
            }
        }
    }
    ${APPOINTMENT_FRAGMENT}
`;

const updateMasterAppointments = (
    store,
    master,
    salon,
    appointment,
    fromDate,
    toDate,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables: {
                master,
                salon,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                withMaster: true,
                showArchive,
                lang: currentLanguage,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.master &&
            data.viewer.salon.master.appointments
        ) {
            let updatedAppointments;

            if (master === appointment.master.id) {
                updatedAppointments = data.viewer.salon.master.appointments.map(
                    item => {
                        if (item.id === appointment.id) {
                            return {
                                ...item,
                                client: appointment.client,
                                duration: appointment.duration,
                                endAt: appointment.endAt,
                                notes: appointment.notes,
                                service: appointment.service,
                                startAt: appointment.startAt,
                            };
                        }
                        return item;
                    },
                );
            } else {
                updatedAppointments = data.viewer.salon.master.appointments.filter(
                    item => item.id !== appointment.id,
                );
            }

            store.writeQuery({
                query: GET_MASTER_APPOINTMENTS_QUERY,
                variables: {
                    master,
                    salon,
                    fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                    toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                    withMaster: true,
                    showArchive,
                    lang: currentLanguage,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            master: {
                                ...data.viewer.salon.master,
                                appointments: updatedAppointments,
                            },
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const updateWorkingDays = (
    store,
    salon,
    master,
    appointment,
    ownAppointment,
) => {
    const fromDate = format(new Date(), 'yyyy-MM-dd');
    const toDate = format(addMonths(startOfMonth(new Date()), 6), 'yyyy-MM-dd');
    try {
        const data = store.readQuery({
            query: GET_WORKING_DAYS,
            variables: {
                salon,
                master,
                fromDate,
                toDate,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.workingDays
        ) {
            let workingDays = [...data.viewer.salon.workingDays];
            const oldDateStr = format(new Date(ownAppointment.startAt), 'yyyy-MM-dd');
            const newDateStr = format(new Date(appointment.startAt), 'yyyy-MM-dd');

            const oldDateIndex = workingDays.findIndex(
                item => item.date === oldDateStr,
            );
            const oldDate = oldDateIndex !== -1 ? workingDays[oldDateIndex] : null;

            if (appointment.master.id === master) {
                const newDateIndex = workingDays.findIndex(
                    item => item.date === newDateStr,
                );
                const newDate = newDateIndex !== -1 ? workingDays[newDateIndex] : null;

                if (newDate) {
                    if (newDate.date !== (oldDate ? oldDate.date : '')) {
                        if (oldDate && oldDate.appointmentCount > 1) {
                            workingDays[oldDateIndex] = {
                                ...oldDate,
                                appointmentCount: oldDate.appointmentCount - 1,
                            };
                            workingDays[newDateIndex] = {
                                ...newDate,
                                appointmentCount: newDate.appointmentCount + 1,
                            };
                        } else {
                            workingDays = workingDays.filter(
                                item => !oldDate || item.date !== oldDate.date,
                            );
                            const nIndex = workingDays.findIndex(
                                item => item.date === newDateStr,
                            );
                            if (nIndex !== -1) {
                                workingDays[nIndex] = {
                                    ...workingDays[nIndex],
                                    appointmentCount: workingDays[nIndex].appointmentCount + 1,
                                };
                            }
                        }
                    }
                } else {
                    if (oldDate && oldDate.appointmentCount > 1) {
                        workingDays[oldDateIndex] = {
                            ...oldDate,
                            appointmentCount: oldDate.appointmentCount - 1,
                        };
                    } else {
                        workingDays = workingDays.filter(
                            item => !oldDate || item.date !== oldDate.date,
                        );
                    }
                    workingDays.push({
                        date: newDateStr,
                        appointmentCount: 1,
                        __typename: 'WorkingDay',
                    });
                }
            } else {
                if (oldDate && oldDate.appointmentCount > 1) {
                    workingDays[oldDateIndex] = {
                        ...oldDate,
                        appointmentCount: oldDate.appointmentCount - 1,
                    };
                } else {
                    workingDays = workingDays.filter(
                        item => !oldDate || item.date !== oldDate.date,
                    );
                }
            }

            store.writeQuery({
                query: GET_WORKING_DAYS,
                variables: {
                    salon,
                    master,
                    fromDate,
                    toDate,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            workingDays,
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const ErrorMessage = ({ error }) => (
    <Message error>
        <Icon name="warning sign" />
        {error}
    </Message>
);

function getInitialValues(appointment, inCabinet) {
    return {
        cabinet: inCabinet ? get(appointment, 'cabinet.id') : null,
        master: get(appointment, 'master.id'),
        client: appointment.client && appointment.client.id,
        startDate: format(startOfDay(new Date(appointment.startAt)), 'yyyy-MM-dd'),
        startTime: Math.abs(
            differenceInMinutes(
                startOfDay(new Date(appointment.startAt)),
                new Date(appointment.startAt),
            ),
        ),
        duration: appointment.duration,
        service: appointment.service.id,
        notes: appointment.notes,
        services: [],
    };
}

type EditAppointmentProps = {
    appointment: Appointment,
    onClose: Function,
    onCreateInvoice: Function,
    updateAppointmentClient: Function,
    archiveAppointment: Function,
    masterAppointments: any,
    fromDate: string | Date,
    toDate: string | Date,
    inCabinet: boolean,
    setDisableInvoice: Function,
    intl: Object,
};

function EditAppointment({
    appointment,
    onClose,
    onCreateInvoice,
    updateAppointmentClient,
    archiveAppointment,
    masterAppointments,
    fromDate,
    toDate,
    inCabinet,
    setDisableInvoice,
    intl,
}: EditAppointmentProps) {
    const salonId = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));
    const enableArchive = useSelector(({ salon: s }) => s.get('settings').enableArchive);
    const isSysadmin = useSelector(({ user }) => user.get('isSysadmin'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const isMaster = useSelector(({ user }) => user.get('isMaster'));

    const isPaid = get(appointment, 'invoice.paid');
    const isGrouped = get(appointment, 'group');

    const scheduleEndAt = useSelector(({ salon: s }) =>
        get(s.get('settings'), 'endAt'),
    );

    const methods = useForm({
        defaultValues: getInitialValues(appointment, inCabinet),
        resolver: editFormValidate,
        context: { intl, inCabinet, scheduleEndAt },
    });
    const {
        handleSubmit,
        reset,
        setError,
        formState: { isSubmitting, errors, isDirty },
    } = methods;

    // Communicate dirty state to parent (for CreateInvoiceButton disabled state)
    useEffect(() => {
        if (setDisableInvoice) {
            setDisableInvoice(isDirty);
        }
    }, [isDirty, setDisableInvoice]);

    // Reset when appointment changes (e.g. navigating between appointments)
    useEffect(() => {
        reset(getInitialValues(appointment, inCabinet));
    }, [appointment && appointment.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateAppointmentMutation] = useMutation(UPDATE_APPOINTMENT_QUERY);

    const onSubmit = async formData => {
        const servicesWithAssignedTime = map(formData.services, service => {
            const newService = cloneDeep(service);
            const formattedTime = format(
                convertMinutesToDateTime(newService.startTime, formData.startDate),
                'yyyy-MM-dd HH:mm',
            );
            newService.startTime = formattedTime;
            return newService;
        });

        const formDataWithServices = { ...formData };
        if (formData.services) {
            formDataWithServices.services = servicesWithAssignedTime;
        }

        try {
            await updateAppointmentMutation({
                variables: {
                    input: {
                        appointmentId: appointment.id,
                        ...omit(formDataWithServices, ['startDate', 'startTime']),
                        startAt: format(
                            convertMinutesToDateTime(
                                formDataWithServices.startTime,
                                formDataWithServices.startDate,
                            ),
                            'yyyy-MM-dd HH:mm',
                        ),
                    },
                    lang: currentLanguage,
                    salon: salonId,
                },
                update: (store, { data: { updateAppointment } }) => {
                    if (masterAppointments) {
                        updateMasterAppointments(
                            store,
                            appointment.master.id,
                            salonId,
                            updateAppointment.appointment,
                            fromDate,
                            toDate,
                            showArchive,
                            currentLanguage,
                        );
                        updateWorkingDays(
                            store,
                            salonId,
                            appointment.master.id,
                            updateAppointment.appointment,
                            appointment,
                        );
                    }
                },
            });
            // Make the just-saved values the pristine baseline (so isDirty
            // clears and CreateInvoiceButton re-enables) and close the dialog.
            reset(formData);
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                const errMsg = get(graphQLError, 'data.error');
                if (graphQLError.name === 'NotExists' && errMsg === 'ID_DOES_NOT_EXISTS') {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.existingsError),
                    });
                    return;
                } else if (
                    graphQLError.name === 'AlreadyExists' &&
                    errMsg === 'TIME_OVERLAPS_WITH_OTHER_RECORD'
                ) {
                    setError('startTime', {
                        message: intl.formatMessage(intlMessages.timeOverlapError),
                    });
                    return;
                } else if (errMsg === 'APPOINTMENT_ALREADY_EXISTS') {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.appointmentAlreadyExists),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    {errors.root && <ErrorMessage error={errors.root.message} />}
                    {isPaid && <InvoiceMessage />}
                    {isGrouped && <GroupedMessage />}
                    <AppointmentForm
                        appointment={appointment}
                        disabled={isPaid}
                        updateAppointmentClient={updateAppointmentClient}
                        onCloseDialog={onClose}
                        inCabinet={inCabinet}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isPaid || !isDirty || isSubmitting}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.appointments.details.EditAppointment.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.appointments.details.EditAppointment.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                    {(enableArchive || isSysadmin || isOwner) &&
                        isPaid &&
                        !isMaster && (
                            <ArchiveButton
                                appointment={appointment}
                                onClose={onClose}
                                masterAppointments={masterAppointments}
                                fromDate={fromDate}
                                toDate={toDate}
                                archiveAppointment={archiveAppointment}
                            />
                        )}
                    {!appointment.invoice && (
                        <CreateInvoiceButton
                            appointment={appointment}
                            onClicked={onCreateInvoice}
                            disabled={isDirty}
                        />
                    )}
                    {!isPaid && (
                        <DeleteAppointmentButton
                            appointment={appointment}
                            onClose={onClose}
                            masterAppointments={masterAppointments}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    )}
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(EditAppointment);
